import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { RoleGuard } from '@/routes/RoleGuard';

// ──────────────────────────────────────────────
// Lazy-loaded pages (code-split per route)
// ──────────────────────────────────────────────

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const WorkOrdersPage = lazy(() => import('@/pages/WorkOrdersPage'));
const WorkOrderFormPage = lazy(() => import('@/pages/WorkOrderFormPage'));
const WorkOrderDetailPage = lazy(() => import('@/pages/WorkOrderDetailPage'));
const MyWorkPage = lazy(() => import('@/pages/MyWorkPage'));
const CustomersPage = lazy(() => import('@/pages/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@/pages/CustomerDetailPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const TeamPage = lazy(() => import('@/pages/TeamPage'));
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage'));
const EstimatesPage = lazy(() => import('@/pages/EstimatesPage'));
const InventoryPage = lazy(() => import('@/pages/InventoryPage'));
const ContractsPage = lazy(() => import('@/pages/ContractsPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const PortalPage = lazy(() => import('@/pages/PortalPage'));
const CustomerPortalPage = lazy(() => import('@/pages/portal/CustomerPortalPage'));
const PortalInvoicePage = lazy(() => import('@/pages/portal/PortalInvoicePage'));
const CsatSurveyPage = lazy(() => import('@/pages/portal/CsatSurveyPage'));
const BookingWidgetPage = lazy(() => import('@/pages/BookingWidgetPage'));
const BeforeAfterReportPage = lazy(() => import('@/pages/BeforeAfterReportPage'));
const TrackingPage = lazy(() => import('@/pages/TrackingPage'));
const DispatchBoardPage = lazy(() => import('@/pages/DispatchBoardPage'));
const CsatDashboardPage = lazy(() => import('@/pages/CsatDashboardPage'));
const LeaderboardPage = lazy(() => import('@/pages/LeaderboardPage'));
const BookingsPage = lazy(() => import('@/pages/BookingsPage'));
const ContractDetailPage = lazy(() => import('@/pages/ContractDetailPage'));

// ──────────────────────────────────────────────
// Route-level loading fallback
// ──────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex h-[50vh] items-center justify-center">
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:0ms]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:150ms]" />
        <div className="h-2 w-2 animate-bounce rounded-full bg-indigo-400 [animation-delay:300ms]" />
      </div>
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ──────────────────────────────────────────────
// Router configuration
// ──────────────────────────────────────────────

export const router = createBrowserRouter([
  // ── Public routes (no auth required) ──────
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/register',
    element: (
      <SuspenseWrapper>
        <RegisterPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <SuspenseWrapper>
        <ForgotPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/accept-invite',
    element: (
      <SuspenseWrapper>
        <AcceptInvitePage />
      </SuspenseWrapper>
    ),
  },

  // ── Public portal (no auth) ──────
  {
    path: '/portal/:token',
    element: (
      <SuspenseWrapper>
        <PortalPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/customer/:token',
    element: (
      <SuspenseWrapper>
        <CustomerPortalPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/customer/:token/invoices/:invoiceId',
    element: (
      <SuspenseWrapper>
        <PortalInvoicePage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/survey/:token',
    element: (
      <SuspenseWrapper>
        <CsatSurveyPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/book/:orgSlug',
    element: (
      <SuspenseWrapper>
        <BookingWidgetPage />
      </SuspenseWrapper>
    ),
  },

  // ── Protected routes (auth required) ──────
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'work-orders',
        element: (
          <RoleGuard requiredPermissions={['work_orders:view']}>
            <SuspenseWrapper>
              <WorkOrdersPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'work-orders/new',
        element: (
          <RoleGuard requiredPermissions={['work_orders:create']}>
            <SuspenseWrapper>
              <WorkOrderFormPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'work-orders/:id',
        element: (
          <RoleGuard requiredPermissions={['work_orders:view']}>
            <SuspenseWrapper>
              <WorkOrderDetailPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'work-orders/:id/edit',
        element: (
          <RoleGuard requiredPermissions={['work_orders:edit']}>
            <SuspenseWrapper>
              <WorkOrderFormPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'work-orders/:id/report',
        element: (
          <RoleGuard requiredPermissions={['work_orders:view']}>
            <SuspenseWrapper>
              <BeforeAfterReportPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'my-work',
        element: (
          <RoleGuard requiredPermissions={['work_orders:view']}>
            <SuspenseWrapper>
              <MyWorkPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'customers',
        element: (
          <RoleGuard requiredPermissions={['customers:view']}>
            <SuspenseWrapper>
              <CustomersPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'customers/:id',
        element: (
          <RoleGuard requiredPermissions={['customers:view']}>
            <SuspenseWrapper>
              <CustomerDetailPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'calendar',
        element: (
          <RoleGuard requiredPermissions={['calendar:view']}>
            <SuspenseWrapper>
              <CalendarPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'team',
        element: (
          <RoleGuard requiredPermissions={['team:view']}>
            <SuspenseWrapper>
              <TeamPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'invoices',
        element: (
          <RoleGuard requiredPermissions={['invoices:view']}>
            <SuspenseWrapper>
              <InvoicesPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'estimates',
        element: (
          <RoleGuard requiredPermissions={['estimates:view']}>
            <SuspenseWrapper>
              <EstimatesPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'contracts',
        element: (
          <RoleGuard requiredPermissions={['contracts:view']}>
            <SuspenseWrapper>
              <ContractsPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'contracts/:id',
        element: (
          <RoleGuard requiredPermissions={['contracts:view']}>
            <SuspenseWrapper>
              <ContractDetailPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'dispatch',
        element: (
          <RoleGuard requiredPermissions={['calendar:view']}>
            <SuspenseWrapper>
              <DispatchBoardPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'tracking',
        element: (
          <RoleGuard requiredPermissions={['tracking:view']}>
            <SuspenseWrapper>
              <TrackingPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'reports/csat',
        element: (
          <RoleGuard requiredPermissions={['csat:view']}>
            <SuspenseWrapper>
              <CsatDashboardPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'leaderboard',
        element: (
          <RoleGuard requiredPermissions={['gamification:view']}>
            <SuspenseWrapper>
              <LeaderboardPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'bookings',
        element: (
          <RoleGuard requiredPermissions={['bookings:view']}>
            <SuspenseWrapper>
              <BookingsPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'inventory',
        element: (
          <RoleGuard requiredPermissions={['inventory:view']}>
            <SuspenseWrapper>
              <InventoryPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'reports',
        element: (
          <RoleGuard requiredPermissions={['reports:view']}>
            <SuspenseWrapper>
              <ReportsPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'settings/*',
        element: (
          <RoleGuard requiredPermissions={['settings:view']}>
            <SuspenseWrapper>
              <SettingsPage />
            </SuspenseWrapper>
          </RoleGuard>
        ),
      },
    ],
  },

  // ── Catch-all 404 ─────────────────────────
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
]);
